import React from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ContentType, CONTENT_TYPE_OPTIONS } from '@/types/mcpTool';
import { FileJson, FileText, Upload, Code } from 'lucide-react';

interface ContentTypeSelectorProps {
  value: ContentType;
  onChange: (contentType: ContentType) => void;
  disabled?: boolean;
}

/**
 * ContentTypeSelector Component
 *
 * Selector for API request content types
 * Features:
 * - Visual icons for each content type
 * - Descriptions to help users choose
 * - Disabled state for methods that don't support it
 */
export const ContentTypeSelector: React.FC<ContentTypeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const getIcon = (contentType: string) => {
    switch (contentType) {
      case 'application/json':
        return <FileJson className="h-4 w-4 text-blue-600" />;
      case 'application/x-www-form-urlencoded':
        return <FileText className="h-4 w-4 text-green-600" />;
      case 'multipart/form-data':
        return <Upload className="h-4 w-4 text-purple-600" />;
      case 'text/plain':
        return <FileText className="h-4 w-4 text-gray-600" />;
      case 'application/graphql':
        return <Code className="h-4 w-4 text-pink-600" />;
      default:
        return <FileJson className="h-4 w-4" />;
    }
  };

  const getContentTypeBadgeColor = (contentType: string) => {
    switch (contentType) {
      case 'application/json':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'application/x-www-form-urlencoded':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'multipart/form-data':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'text/plain':
        return 'bg-gray-50 text-gray-700 border-gray-200';
      case 'application/graphql':
        return 'bg-pink-50 text-pink-700 border-pink-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="content-type">Content Type</Label>
      <div className="flex items-center gap-2">
        <Select value={value} onValueChange={(v) => onChange(v as ContentType)} disabled={disabled}>
          <SelectTrigger id="content-type" className="flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CONTENT_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <div className="flex items-center gap-2">
                  {getIcon(option.value)}
                  <div>
                    <div className="font-medium">{option.label}</div>
                    <div className="text-xs text-gray-500">{option.description}</div>
                  </div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Badge variant="outline" className={getContentTypeBadgeColor(value)}>
          {getIcon(value)}
          <span className="ml-1">{CONTENT_TYPE_OPTIONS.find((o) => o.value === value)?.label}</span>
        </Badge>
      </div>
      <p className="text-xs text-gray-500">
        Choose how the request body should be formatted
      </p>
    </div>
  );
};
